import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import { MAX_DISPLAY_FILE_SIZE } from 'shared/domain/document/documentLimits';
import { DocumentUrlsModel } from 'shared/domain/document/documentUrlsModel';
import { DisplayedFile, DisplayedFilesStore } from 'components/common/gallery/filesStore';
import {
  FILE_STATE_KIND,
  FileReadSuccess,
} from 'components/common/graphicUploader/types';
import { ImageConvertStatus, mergeImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { isBlobUrl } from 'shared/utils/blobUrl';
import { isUsableSource } from 'shared/utils/sourceUrl';
import type { DocumentModel } from 'shared/domain/document/documentModel';
import { mergeDocumentUrlsModel } from 'shared/domain/document/mergeDocumentUrlsModel';

type DrawingRevisionSnapshot = {
  drawingSyncStatus?: SyncStatus;
  data?: {
    isDrawn?: boolean;
    mergedSrc?: string;
    drawingSrc?: string;
    thumbnailMergedSrc?: string;
  };
};

/**
 * Detects remote drawing overlay changes only.
 * Must NOT use generic `modifiedAt` — every upload/sync bumps it and would
 * falsely invalidate previews (PT-6854 regression).
 */
export function hasRemoteDrawingRevisionChanged(
  previousFile: DrawingRevisionSnapshot,
  nextFile: DrawingRevisionSnapshot
): boolean {
  if (previousFile.data?.isDrawn !== nextFile.data?.isDrawn) {
    return true;
  }

  if (previousFile.drawingSyncStatus !== nextFile.drawingSyncStatus) {
    return true;
  }

  if (previousFile.data?.mergedSrc !== nextFile.data?.mergedSrc) {
    return true;
  }

  if (previousFile.data?.drawingSrc !== nextFile.data?.drawingSrc) {
    return true;
  }

  if (
    previousFile.data?.thumbnailMergedSrc !== nextFile.data?.thumbnailMergedSrc
  ) {
    return true;
  }

  return false;
}

function toDrawingRevisionSnapshot(document: DocumentModel): DrawingRevisionSnapshot {
  return {
    drawingSyncStatus: document.drawingSyncStatus,
    data: {
      isDrawn: document.data?.isDrawn,
      mergedSrc: document.data?.mergedSrc,
      drawingSrc: document.data?.drawingSrc,
      thumbnailMergedSrc: document.data?.thumbnailMergedSrc,
    },
  };
}

/** Whether resolved preview cache should be invalidated after a document list sync. */
export function hasDocumentPreviewCacheRevisionChanged(
  previous: DocumentModel,
  next: DocumentModel
): boolean {
  if (previous._id !== next._id) {
    return true;
  }
  if (previous.syncStatus !== next.syncStatus) {
    return true;
  }
  if (previous.imageConvertStatus !== next.imageConvertStatus) {
    return true;
  }
  return hasRemoteDrawingRevisionChanged(
    toDrawingRevisionSnapshot(previous),
    toDrawingRevisionSnapshot(next)
  );
}

function hasUsableLocalPreviewSource(file: DisplayedFile): boolean {
  return (
    isUsableSource(file.downloadSrc) ||
    isUsableSource(file.src) ||
    isUsableSource(file.thumbnail)
  );
}

/**
 * PT-6854: merged/drawing signed URLs are stale after a drawing save, but the
 * base file blob preview must stay visible while fresh URLs are fetched.
 */
export function invalidateStaleDrawingSignedUrls(
  filesStore: DisplayedFilesStore,
  localId: number
): void {
  const files = filesStore.get();
  const index = files.findIndex((file) => file.localId === localId);
  if (index === -1) {
    return;
  }

  filesStore.set(
    files.map((currentFile, fileIndex) => {
      if (fileIndex !== index) {
        return currentFile;
      }

      return {
        ...currentFile,
        loading: hasUsableLocalPreviewSource(currentFile) ? false : true,
        signedRequest: undefined,
      };
    })
  );
}

// Note: filesStore is a custom gallery-local store, not a Zustand store.

interface SetFileSignedRequestParams {
  fileId: number | undefined;
  filesStore: DisplayedFilesStore;
  fileSelector: (file: DisplayedFile) => number | undefined;
  thumbnail: string;
  downloadSrc: string | undefined;
  src: string | undefined;
  signedRequest: DocumentUrlsModel;
}

export function setFileSignedRequest({
  fileId,
  filesStore,
  fileSelector,
  thumbnail,
  downloadSrc,
  src,
  signedRequest,
}: SetFileSignedRequestParams): void {
  if (fileId === undefined) return;

  const files = filesStore.get();
  const fileIndex = files.findIndex((file) => fileSelector(file) === fileId);
  const file = files[fileIndex];
  if (fileIndex === -1 || !file) {
    return;
  }

  const mergedSignedRequest = mergeDocumentUrlsModel(
    file.signedRequest,
    signedRequest
  );

  const imageConvertStatus = mergedSignedRequest.imageSignedRequest
    ? ImageConvertStatus.SUCCESS
    : file.imageConvertStatus;

  files[fileIndex] = {
    ...file,
    fileStateKind: FILE_STATE_KIND.SAVED,
    loading: false,
    downloadSrc:
      downloadSrc && downloadSrc !== DOCUMENT_ERROR_SOURCE
        ? downloadSrc
        : file.downloadSrc,
    thumbnail:
      thumbnail && thumbnail !== DOCUMENT_ERROR_SOURCE
        ? thumbnail
        : file.thumbnail,
    src:
      src && src !== DOCUMENT_ERROR_SOURCE ? src : file.src,
    signedRequest: mergedSignedRequest,
    imageConvertStatus,
  };

  filesStore.set(files.slice());
}

export function markFileAsNotLoading(
  fileToMark: DisplayedFile<FileReadSuccess | object>,
  filesStore: DisplayedFilesStore,
  fileSelector?: (file: DisplayedFile) => number | undefined
): void {
  const files = filesStore.get();
  let fileIndex = files.findIndex((item) => item === fileToMark);

  if (fileIndex < 0 && fileSelector) {
    const targetId = fileSelector(fileToMark);
    if (targetId !== undefined) {
      fileIndex = files.findIndex((f) => fileSelector(f) === targetId);
    }
  }

  if (fileIndex < 0) {
    return;
  }

  if (files[fileIndex].loading === false) {
    return;
  }

  files[fileIndex] = {
    ...files[fileIndex],
    loading: false,
  };

  filesStore.set(files.slice());
}

export function resolveUnsavedFileForDisplay(
  file: DisplayedFile<FileReadSuccess>,
  filesStore: DisplayedFilesStore
): void {
  const files = filesStore.get();
  const fileIndex = files.findIndex((_f) => file === _f);
  const foundFile = files[fileIndex];

  if (!foundFile) return;

  const sourceFileSize = foundFile.file?.size ?? file.file?.size;
  const thumbnail =
    sourceFileSize !== undefined && sourceFileSize < MAX_DISPLAY_FILE_SIZE
      ? foundFile.src ?? file.src
      : DOCUMENT_ERROR_SOURCE;

  files[fileIndex] = {
    ...foundFile,
    fileStateKind: FILE_STATE_KIND.UNSAVED,
    loading: false,
    thumbnail,
  };

  filesStore.set(files.slice());
}

export { mergeImageConvertStatus };

export function preserveResolvedPreviewState<T extends DisplayedFile>(
  previousFiles: DisplayedFile[],
  nextFiles: T[],
  options?: {
    previousScopeKey?: string;
    nextScopeKey?: string;
  }
): T[] {
  if (
    options?.previousScopeKey &&
    options?.nextScopeKey &&
    options.previousScopeKey !== options.nextScopeKey
  ) {
    return nextFiles;
  }

  const previousByRemoteId = new Map<string, DisplayedFile>();
  const previousByLocalId = new Map<number, DisplayedFile>();

  previousFiles.forEach((file) => {
    if (typeof file._id === 'string' && file._id.length > 0) {
      previousByRemoteId.set(file._id, file);
    }

    if (
      typeof file.localId === 'number' &&
      Number.isFinite(file.localId)
    ) {
      previousByLocalId.set(file.localId, file);
    }
  });

  return nextFiles.map((file) => {
    const previousFile =
      (typeof file._id === 'string' && file._id.length > 0
        ? previousByRemoteId.get(file._id)
        : undefined) ||
      (typeof file.localId === 'number' && Number.isFinite(file.localId)
        ? previousByLocalId.get(file.localId)
        : undefined);

    const fileWithMergedStatus = previousFile
      ? {
          ...file,
          imageConvertStatus: mergeImageConvertStatus(
            file.imageConvertStatus,
            previousFile.imageConvertStatus
          ),
          pdfPageCount: file.pdfPageCount ?? previousFile.pdfPageCount,
        }
      : file;

    if (!previousFile) {
      return fileWithMergedStatus;
    }

    const previousDownloadSrcIsUsable = Boolean(
      previousFile.downloadSrc &&
        previousFile.downloadSrc !== DOCUMENT_ERROR_SOURCE
    );
    const previousThumbnailIsUsable = Boolean(
      previousFile.thumbnail &&
        previousFile.thumbnail !== DOCUMENT_ERROR_SOURCE
    );
    const previousSrcIsUsable = Boolean(
      previousFile.src && previousFile.src !== DOCUMENT_ERROR_SOURCE
    );

    const hadResolvedPreview = Boolean(
      previousFile.signedRequest ||
        previousDownloadSrcIsUsable ||
        previousThumbnailIsUsable ||
        previousSrcIsUsable
    );

    if (!hadResolvedPreview) {
      return file;
    }

    const isPersistedFile =
      typeof file._id === 'string' && file._id.length > 0;

    const shouldPreferNextRemoteSource =
      isPersistedFile &&
      ((typeof file.src === 'string' && !isBlobUrl(file.src)) ||
        (typeof file.downloadSrc === 'string' &&
          !isBlobUrl(file.downloadSrc)));

    const drawingRevisionChanged = hasRemoteDrawingRevisionChanged(
      previousFile,
      fileWithMergedStatus
    );

    if (drawingRevisionChanged) {
      const preserveLocalPreview =
        hadResolvedPreview && !shouldPreferNextRemoteSource;

      return {
        ...fileWithMergedStatus,
        loading: preserveLocalPreview ? false : true,
        signedRequest: undefined,
        downloadSrc: preserveLocalPreview
          ? (previousDownloadSrcIsUsable
              ? previousFile.downloadSrc
              : undefined) ?? fileWithMergedStatus.downloadSrc
          : shouldPreferNextRemoteSource
            ? fileWithMergedStatus.downloadSrc ??
              (previousDownloadSrcIsUsable ? previousFile.downloadSrc : undefined)
            : (previousDownloadSrcIsUsable
                ? previousFile.downloadSrc
                : undefined) ?? fileWithMergedStatus.downloadSrc,
        thumbnail:
          (previousThumbnailIsUsable ? previousFile.thumbnail : undefined) ??
          fileWithMergedStatus.thumbnail,
        src: preserveLocalPreview
          ? (previousSrcIsUsable ? previousFile.src : undefined) ??
            fileWithMergedStatus.src
          : shouldPreferNextRemoteSource
            ? fileWithMergedStatus.src ??
              (previousSrcIsUsable ? previousFile.src : undefined)
            : (previousSrcIsUsable ? previousFile.src : undefined) ??
              fileWithMergedStatus.src,
      };
    }

    return {
      ...fileWithMergedStatus,
      loading: false,
      signedRequest:
        fileWithMergedStatus.signedRequest ?? previousFile.signedRequest,
      downloadSrc: shouldPreferNextRemoteSource
        ? fileWithMergedStatus.downloadSrc ??
          (previousDownloadSrcIsUsable ? previousFile.downloadSrc : undefined)
        : (previousDownloadSrcIsUsable
            ? previousFile.downloadSrc
            : undefined) ?? fileWithMergedStatus.downloadSrc,
      thumbnail:
        (previousThumbnailIsUsable ? previousFile.thumbnail : undefined) ??
        fileWithMergedStatus.thumbnail,
      src: shouldPreferNextRemoteSource
        ? fileWithMergedStatus.src ??
          (previousSrcIsUsable ? previousFile.src : undefined)
        : (previousSrcIsUsable ? previousFile.src : undefined) ??
          fileWithMergedStatus.src,
    };
  });
}
